import React, { useState, useRef, useEffect, useCallback, useMemo, useLayoutEffect } from 'react';
import { X, Volume2, VolumeX, Play, Share2, Eye, Check, ExternalLink } from 'lucide-react';
import { getAdSessionId } from './VideoAd';

const API_URL = process.env.REACT_APP_API_URL || '';

// Interstitial ads are spliced in every 4-6 real videos (randomized per gap) so the
// pattern isn't perfectly predictable. Skipped entirely for premium users.
const AD_INTERVAL_MIN = 4;
const AD_INTERVAL_MAX = 6;

/*
 * FeedView — een verticale, full-screen video-feed in RedGifs/TikTok-stijl.
 *
 * Props:
 *   videos        : de bestaande videos-array uit App.js
 *   startIndex    : bij welke video te beginnen (default 0)
 *   onClose       : sluit de feed
 *   onLoadMore    : callback om de volgende pagina te laden (hergebruikt fetchVideos)
 *   hasMore       : of er nog meer video's te laden zijn
 *
 * Werkt op de bestaande data — geen nieuwe API-logica. Video's streamen via
 * de directe /media/<id>.mp4 link (sneller dan /stream voor autoplay).
 */
export default function FeedView({ videos, startIndex = 0, onClose, onLoadMore, hasMore, filterLabel = null, onTagClick = null, isPremium = false }) {
  const containerRef = useRef(null);
  const videoRefs = useRef({});           // slide index -> <video> element (real videos + video-type ads)
  const [activeIndex, setActiveIndex] = useState(startIndex);
  const [muted, setMuted] = useState(true);
  const [sharedId, setSharedId] = useState(null);
  const [tagsByVideo, setTagsByVideo] = useState({});   // videoId -> [ {id,name,tag_type} ]

  // Ad-slot boundaries (counts of real videos after which an ad slide is inserted).
  // Kept in a ref and only ever extended forward as `videos` grows, so pagination
  // never reshuffles ad positions the user has already scrolled past.
  const adBoundariesRef = useRef([]);
  const [boundariesVersion, setBoundariesVersion] = useState(0);

  useLayoutEffect(() => {
    if (isPremium) return;
    let last = adBoundariesRef.current.length ? adBoundariesRef.current[adBoundariesRef.current.length - 1] : 0;
    if (last >= videos.length) return;
    const boundaries = [...adBoundariesRef.current];
    while (last < videos.length) {
      last += AD_INTERVAL_MIN + Math.floor(Math.random() * (AD_INTERVAL_MAX - AD_INTERVAL_MIN + 1));
      boundaries.push(last);
    }
    adBoundariesRef.current = boundaries;
    setBoundariesVersion((v) => v + 1);
  }, [videos.length, isPremium]);

  // Interleaves ad slides into the video list at the precomputed boundaries.
  const feedItems = useMemo(() => {
    if (isPremium) return videos.map((video, i) => ({ type: 'video', video, videoIndex: i }));
    const boundarySet = new Set(adBoundariesRef.current);
    const items = [];
    videos.forEach((video, i) => {
      items.push({ type: 'video', video, videoIndex: i });
      if (boundarySet.has(i + 1)) items.push({ type: 'ad', key: `ad-${i}` });
    });
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videos, isPremium, boundariesVersion]);

  // Laad de echte tags (uit video_tags) voor de actieve + aangrenzende video's.
  useEffect(() => {
    const toLoad = [activeIndex - 1, activeIndex, activeIndex + 1]
      .map((i) => feedItems[i])
      .filter((item) => item && item.type === 'video')
      .map((item) => item.video)
      .filter((v) => !tagsByVideo[v.id]);
    toLoad.forEach(async (v) => {
      try {
        const res = await fetch(`${API_URL}/api/videos/${v.id}/tags`);
        const data = await res.json();
        setTagsByVideo((prev) => ({ ...prev, [v.id]: data.tags || [] }));
      } catch (e) { /* stil */ }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, feedItems]);

  // --- Delen: native share sheet op mobiel, klembord op desktop ------------
  const shareVideo = async (video) => {
    const url = `${window.location.origin}/v/${video.id}`;
    const title = video.title || 'VideoShare';
    // Native deel-menu (mobiel / ondersteunde browsers)
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (e) {
        // gebruiker annuleerde of niet ondersteund → val terug op klembord
      }
    }
    // Klembord-fallback (desktop)
    try {
      await navigator.clipboard.writeText(url);
      setSharedId(video.id);
      setTimeout(() => setSharedId(null), 1800);
    } catch (e) {
      // laatste redmiddel: toon de link zodat de gebruiker 'm handmatig kopieert
      window.prompt('Kopieer de link:', url);
    }
  };

  // --- Autoplay: speel de zichtbare video, pauzeer de rest -----------------
  // Een IntersectionObserver bepaalt welke video het meest in beeld is.
  useEffect(() => {
    const options = { root: containerRef.current, threshold: 0.6 };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const idx = Number(entry.target.dataset.index);
        const item = feedItems[idx];
        const vid = videoRefs.current[idx];
        if (entry.isIntersecting) {
          // Always track the active slide, even for image ads (no <video> element).
          setActiveIndex(idx);
          if (vid) {
            vid.currentTime = 0;
            vid.play().catch(() => {});
          }
          // Trigger het laden van de volgende pagina als we bijna onderaan zijn
          if (item && item.type === 'video' && item.videoIndex >= videos.length - 3 && hasMore) {
            onLoadMore && onLoadMore();
          }
        } else if (vid) {
          vid.pause();
        }
      });
    }, options);

    // Observeer alle huidige slides
    const slides = containerRef.current?.querySelectorAll('[data-index]') || [];
    slides.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [feedItems, videos.length, hasMore, onLoadMore]);

  // --- Bij openen: scroll direct naar de startvideo ------------------------
  useEffect(() => {
    const el = containerRef.current?.querySelector(`[data-index="${startIndex}"]`);
    if (el) el.scrollIntoView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Geluid togglen geldt voor alle video's ------------------------------
  useEffect(() => {
    Object.values(videoRefs.current).forEach((v) => {
      if (v) v.muted = muted;
    });
  }, [muted, activeIndex]);

  // --- Tik op de video: play/pause toggle ----------------------------------
  const togglePlay = (idx) => {
    const vid = videoRefs.current[idx];
    if (!vid) return;
    if (vid.paused) vid.play().catch(() => {});
    else vid.pause();
  };

  // --- Toetsenbord: pijl omhoog/omlaag + escape ----------------------------
  const handleKey = useCallback((e) => {
    if (e.key === 'Escape') { onClose && onClose(); return; }
    const dir = e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : 0;
    if (dir === 0) return;
    const next = Math.min(Math.max(activeIndex + dir, 0), feedItems.length - 1);
    const el = containerRef.current?.querySelector(`[data-index="${next}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }, [activeIndex, feedItems.length, onClose]);

  useEffect(() => {
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  return (
    <div className="fixed inset-0 z-50 bg-black">
      {/* Filterlabel (alleen bij actief filter) */}
      {filterLabel && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 bg-red-600/90 backdrop-blur px-4 py-1.5 rounded-full text-white text-sm font-medium">
          <span>{filterLabel}</span>
        </div>
      )}

      {/* Sluitknop */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-30 p-2 rounded-full bg-black/50 hover:bg-black/80 text-white transition"
        aria-label="Sluiten"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Geluidsknop (globaal) */}
      <button
        onClick={() => setMuted((m) => !m)}
        className="absolute top-4 left-4 z-30 p-2 rounded-full bg-black/50 hover:bg-black/80 text-white transition"
        aria-label={muted ? 'Geluid aan' : 'Geluid uit'}
      >
        {muted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
      </button>

      {/* De scroll-container met snap */}
      <div
        ref={containerRef}
        className="h-full w-full overflow-y-scroll snap-y snap-mandatory scroll-smooth"
        style={{ scrollbarWidth: 'none' }}
      >
        {feedItems.map((item, idx) => {
          if (item.type === 'ad') {
            return (
              <AdSlide
                key={item.key}
                idx={idx}
                isActive={idx === activeIndex}
                muted={muted}
                registerVideoRef={(el) => { videoRefs.current[idx] = el; }}
              />
            );
          }

          const video = item.video;
          return (
          <div
            key={video.id}
            data-index={idx}
            className="relative h-full w-full snap-start snap-always flex items-center justify-center"
          >
            {/* De video zelf */}
            <video
              ref={(el) => { videoRefs.current[idx] = el; }}
              src={`${API_URL}/media/${video.id}.mp4`}
              poster={`${API_URL}/media/${video.id}.jpg`}
              muted={muted}
              loop
              playsInline
              preload={Math.abs(idx - activeIndex) <= 1 ? 'auto' : 'none'}
              onClick={() => togglePlay(idx)}
              className="max-h-full max-w-full h-full w-full object-contain cursor-pointer"
            />

            {/* Rechter actiebalk (overlay) — één consistent format */}
            <div className="absolute right-3 bottom-24 flex flex-col items-center gap-5 z-20">
              <button
                onClick={() => shareVideo(video)}
                className="flex flex-col items-center text-white"
                aria-label="Delen"
              >
                <span className="p-3 rounded-full bg-black/40 hover:bg-black/70 transition">
                  {sharedId === video.id ? <Check className="w-7 h-7" /> : <Share2 className="w-7 h-7" />}
                </span>
                <span className="text-xs mt-1">{sharedId === video.id ? 'Copied' : 'Share'}</span>
              </button>
              <div className="flex flex-col items-center text-white">
                <span className="p-3 rounded-full bg-black/40">
                  <Eye className="w-7 h-7" />
                </span>
                <span className="text-xs mt-1">{video.views || 0}</span>
              </div>
            </div>

            {/* Onder-info (overlay) */}
            <div className="absolute left-4 right-20 bottom-6 z-30 text-white pointer-events-none">
              <h3 className="font-semibold text-lg drop-shadow mb-1 truncate">{video.title}</h3>
              <p className="text-sm text-white/80 drop-shadow">
                @{video.username}
                {video.actress ? ` • 🎭 ${video.actress}` : ''}
              </p>
              {tagsByVideo[video.id] && tagsByVideo[video.id].length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2 pointer-events-auto">
                  {tagsByVideo[video.id].map((t) => (
                    <button
                      key={t.id}
                      onClick={(e) => { e.stopPropagation(); e.preventDefault(); onTagClick && onTagClick(t.name); }}
                      className="text-xs bg-white/20 hover:bg-red-600 active:bg-red-700 backdrop-blur px-3 py-1 rounded-full transition-colors cursor-pointer"
                    >
                      #{t.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Play-indicator wanneer gepauzeerd (verschijnt kort) */}
            <PausedIndicator videoEl={videoRefs.current[idx]} />
          </div>
          );
        })}

        {/* Laad-indicator onderaan */}
        {hasMore && (
          <div className="h-20 flex items-center justify-center text-white/50 text-sm">
            Loading more…
          </div>
        )}
      </div>
    </div>
  );
}

/* Toont een grote play-driehoek als de bijbehorende video gepauzeerd is. */
function PausedIndicator({ videoEl }) {
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (!videoEl) return;
    const on = () => setPaused(true);
    const off = () => setPaused(false);
    videoEl.addEventListener('pause', on);
    videoEl.addEventListener('play', off);
    return () => {
      videoEl.removeEventListener('pause', on);
      videoEl.removeEventListener('play', off);
    };
  }, [videoEl]);

  if (!paused) return null;
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
      <Play className="w-20 h-20 text-white/80 drop-shadow-lg" fill="currentColor" />
    </div>
  );
}

/*
 * AdSlide — full-screen interstitial ad, styled like a regular feed slide.
 * Fetches its own random ad, fires one impression when it becomes the active
 * slide, and tracks clicks — same accounting as VideoAd.js's player-modal ads.
 */
function AdSlide({ idx, isActive, muted, registerVideoRef }) {
  const [ad, setAd] = useState(null);
  const [loaded, setLoaded] = useState(false);
  // Full-screen slot prefers a portrait/vertical creative when the advertiser
  // supplied one; otherwise falls back to the same landscape asset used in
  // the player-modal banner (will letterbox, but still shows something).
  const mediaSrc = ad ? (ad.media_url_vertical || ad.media_url) : null;
  const sessionId = useRef(getAdSessionId());
  const impressionSent = useRef(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/ads/random`)
      .then((res) => res.json())
      .then((data) => { if (!cancelled) setAd(data.ad || null); })
      .catch(() => { if (!cancelled) setAd(null); })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!isActive || !ad || impressionSent.current) return;
    impressionSent.current = true;
    fetch(`${API_URL}/api/ads/${ad.id}/impression`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId: sessionId.current }),
    }).catch(() => {});
  }, [isActive, ad]);

  const handleClick = () => {
    if (!ad) return;
    fetch(`${API_URL}/api/ads/${ad.id}/click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId: sessionId.current }),
    }).catch(() => {});
    window.open(ad.click_url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div data-index={idx} className="relative h-full w-full snap-start snap-always flex items-center justify-center bg-black">
      <span className="absolute top-4 left-1/2 -translate-x-1/2 z-30 text-xs bg-black/60 px-3 py-1 rounded-full text-zinc-300 uppercase tracking-wide">
        Advertisement
      </span>

      {loaded && !ad && (
        <div className="text-zinc-600 text-sm">No ad to show</div>
      )}

      {ad && ad.ad_type === 'video' && (
        <video
          ref={registerVideoRef}
          src={mediaSrc}
          muted={muted}
          autoPlay
          loop
          playsInline
          onClick={handleClick}
          className="max-h-full max-w-full h-full w-full object-contain cursor-pointer"
        />
      )}

      {ad && ad.ad_type !== 'video' && mediaSrc && (
        <img
          src={mediaSrc}
          alt={ad.title}
          onClick={handleClick}
          className="max-h-full max-w-full object-contain cursor-pointer"
        />
      )}

      {ad && (
        <div className="absolute left-4 right-4 bottom-24 z-30 text-white pointer-events-none">
          <h3 className="font-semibold text-lg drop-shadow mb-1 truncate">{ad.title}</h3>
          {ad.description && <p className="text-sm text-white/80 drop-shadow mb-3">{ad.description}</p>}
          <button
            onClick={handleClick}
            className="pointer-events-auto flex items-center gap-1.5 text-sm bg-red-600 hover:bg-red-700 px-4 py-2 rounded-full font-medium transition-colors"
          >
            <ExternalLink className="w-4 h-4" /> Learn more
          </button>
        </div>
      )}
    </div>
  );
}
